import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { fetchLiveLinkUrl } from "@/hera-api/live-link-api";
import {
  type BrowserHelperConfig,
  isBrowserHelperConfig,
} from "@/lib/court-link-helper";
import {
  LiveLinkFormatError,
  type LiveLinkHttpMethod,
  LiveLinkResolveError,
  buildLiveLinkExtensionHref,
  buildLiveLinkHref,
  copyTextToClipboard,
  courtRecordsUrlFromLiveLink,
  resolveLiveLinkPostHref,
} from "@/lib/live-link";
import type { ComponentProps } from "react";
import { useCallback, useState } from "react";
import { toast } from "sonner";

export interface LiveLinkActionConfig {
  value: string;
  urlTemplate: string | null | undefined;
  copyValueToClipboard: boolean;
  httpMethod?: LiveLinkHttpMethod | string | null;
  valueExtractRegex?: string | null;
  requestFields?: Record<string, string> | null;
  browserHelperConfig?: BrowserHelperConfig | Record<string, unknown> | null;
  blankAnswerUrl?: string | null;
}

function normalizeHttpMethod(
  method: LiveLinkActionConfig["httpMethod"],
): LiveLinkHttpMethod {
  const normalized = String(method ?? "GET")
    .trim()
    .toUpperCase();
  if (normalized === "POST") {
    return "POST";
  }
  if (normalized === "EXTENSION") {
    return "EXTENSION";
  }
  return "GET";
}

export function useLiveLinkAction({
  value,
  urlTemplate,
  copyValueToClipboard,
  httpMethod,
  valueExtractRegex,
  requestFields,
  browserHelperConfig,
  blankAnswerUrl,
}: LiveLinkActionConfig) {
  const method = normalizeHttpMethod(httpMethod);
  let href: string | null = null;
  let buildError: string | null = null;
  let formatError = false;
  try {
    href =
      method === "EXTENSION"
        ? buildLiveLinkExtensionHref(urlTemplate, value, {
            valueExtractRegex,
            requestFields,
            browserHelperConfig: isBrowserHelperConfig(browserHelperConfig)
              ? browserHelperConfig
              : null,
          })
        : buildLiveLinkHref(urlTemplate, value, {
            valueExtractRegex,
            requestFields,
          });
  } catch (error) {
    formatError = error instanceof LiveLinkFormatError;
    buildError =
      error instanceof LiveLinkResolveError
        ? error.message
        : "Unable to build Live Link URL.";
  }
  const unknownFormatHref = formatError
    ? courtRecordsUrlFromLiveLink({
        blankAnswerUrl,
        urlTemplate,
        httpMethod: method,
        browserHelperConfig:
          browserHelperConfig && typeof browserHelperConfig === "object"
            ? browserHelperConfig
            : null,
      })
    : null;

  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isUnknownFormatOpen, setIsUnknownFormatOpen] = useState(false);
  const [isResolving, setIsResolving] = useState(false);

  const openResolved = useCallback(async () => {
    if (buildError) {
      toast.error(buildError);
      return;
    }
    if (!href) {
      return;
    }
    if (method === "GET" || method === "EXTENSION") {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }

    setIsResolving(true);
    try {
      const resultsUrl = await resolveLiveLinkPostHref(href, fetchLiveLinkUrl);
      window.open(resultsUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      const message =
        error instanceof LiveLinkResolveError || error instanceof Error
          ? error.message
          : "Unable to open case records.";
      toast.error(message);
    } finally {
      setIsResolving(false);
    }
  }, [buildError, href, method]);

  const trigger = useCallback(() => {
    if (formatError && unknownFormatHref) {
      void copyTextToClipboard(value);
      setIsUnknownFormatOpen(true);
      return;
    }
    if (buildError) {
      toast.error(buildError);
      return;
    }
    if (!href) {
      return;
    }
    if (copyValueToClipboard) {
      void copyTextToClipboard(value);
      setIsCopyDialogOpen(true);
      return;
    }
    void openResolved();
  }, [
    buildError,
    copyValueToClipboard,
    formatError,
    href,
    openResolved,
    unknownFormatHref,
    value,
  ]);

  const handleProceed = useCallback(() => {
    setIsCopyDialogOpen(false);
    void openResolved();
  }, [openResolved]);

  const handleUnknownFormatProceed = useCallback(() => {
    setIsUnknownFormatOpen(false);
    if (unknownFormatHref) {
      window.open(unknownFormatHref, "_blank", "noopener,noreferrer");
    }
  }, [unknownFormatHref]);

  return {
    href,
    unknownFormatHref,
    isCopyDialogOpen,
    setIsCopyDialogOpen,
    isUnknownFormatOpen,
    setIsUnknownFormatOpen,
    isResolving,
    trigger,
    handleProceed,
    handleUnknownFormatProceed,
  };
}

export function LiveLinkCopyConfirmDialog({
  open,
  onOpenChange,
  value,
  onProceed,
  isResolving = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onProceed: () => void;
  isResolving?: boolean;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Case number copied to clipboard</AlertDialogTitle>
          <AlertDialogDescription>
            &quot;{value}&quot; was copied to your clipboard. Proceed to case
            records when you are ready.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isResolving}>Cancel</AlertDialogCancel>
          <Button type="button" onClick={onProceed} disabled={isResolving}>
            {isResolving ? "Opening…" : "Proceed to case records"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function LiveLinkUnknownFormatDialog({
  open,
  onOpenChange,
  onProceed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProceed: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Unknown case number format copied to dashboard
          </AlertDialogTitle>
          <AlertDialogDescription>
            Court records will open without inserting this case number.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button type="button" onClick={onProceed}>
            Proceed to case records
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function LiveLinkButton({
  value,
  urlTemplate,
  copyValueToClipboard,
  httpMethod,
  valueExtractRegex,
  requestFields,
  browserHelperConfig,
  blankAnswerUrl,
  children,
  ...buttonProps
}: LiveLinkActionConfig &
  ComponentProps<typeof Button> & {
    children: React.ReactNode;
  }) {
  const {
    href,
    unknownFormatHref,
    isCopyDialogOpen,
    setIsCopyDialogOpen,
    isUnknownFormatOpen,
    setIsUnknownFormatOpen,
    isResolving,
    trigger,
    handleProceed,
    handleUnknownFormatProceed,
  } = useLiveLinkAction({
    value,
    urlTemplate,
    copyValueToClipboard,
    httpMethod,
    valueExtractRegex,
    requestFields,
    browserHelperConfig,
    blankAnswerUrl,
  });

  if (!href && !unknownFormatHref && !urlTemplate?.trim()) {
    return null;
  }

  return (
    <>
      <Button
        type="button"
        {...buttonProps}
        onClick={trigger}
        disabled={buttonProps.disabled || isResolving}
      >
        {children}
      </Button>
      {copyValueToClipboard ? (
        <LiveLinkCopyConfirmDialog
          open={isCopyDialogOpen}
          onOpenChange={setIsCopyDialogOpen}
          value={value}
          onProceed={handleProceed}
          isResolving={isResolving}
        />
      ) : null}
      <LiveLinkUnknownFormatDialog
        open={isUnknownFormatOpen}
        onOpenChange={setIsUnknownFormatOpen}
        onProceed={handleUnknownFormatProceed}
      />
    </>
  );
}
